import { apiClient } from '../../lib/util';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

export function useListEmployees(companyId?: string, options = {}) {
  return useInfiniteQuery({
    queryKey: ['employees', companyId ?? 'MISSING_COMPANY_ID'],
    queryFn: async ({ queryKey }) => {
      let allEmployees = [];
      let result = await apiClient.employees.employeesList({
        company: queryKey[1]
      });

      allEmployees = [...result.results];

      while (result.next) {
        const nextOffset = new URL(result.next).searchParams.get('offset');
        result = await apiClient.employees.employeesList({
          company: queryKey[1],
          offset: parseInt(nextOffset ?? '0')
        });
        allEmployees = [...allEmployees, ...result.results];
      }

      return allEmployees;
    },
    getNextPageParam: () => undefined,
    initialPageParam: 0,
    enabled: !!companyId,
    ...options
  });
}

export function useEmployeeBenefitBudgetAndSettings(employeeId: string, options = {}) {
  return useQuery({
    queryKey: ['employee', employeeId, 'employee-benefit-budget'],
    queryFn: async () => {
      const nextMonth = new Date(new Date().setMonth(new Date().getMonth() + 1, 1));
      const [benefitBudget, settings] = await Promise.all([
        apiClient.employees.employeesBenefitBudgetRetrieve({
          id: employeeId,
          accountingPeriodMonth: nextMonth.getMonth() + 1,
          accountingPeriodYear: nextMonth.getFullYear()
        }),
        apiClient.employees.employeesBenefitSettingsRetrieve({ id: employeeId })
      ]);
      return { benefitBudget, settings };
    },
    ...options
  });
}

export function useGetEmployee(employeeId: string, options = {}) {
  return useQuery({
    queryKey: ['employee', employeeId],
    queryFn: () => apiClient.employees.employeesRetrieve({ id: employeeId }),
    ...options
  });
}

export const useGetEmployeeBudgetUpdates = (month: number, year: number, employeeId: string) => {
  return useQuery({
    queryKey: ['budgetUpdates', employeeId, month, year],
    queryFn: async () => {
      const result = await apiClient.employees.employeesBenefitBudgetUpdatesList({
        accountingPeriodMonth: month,
        accountingPeriodYear: year,
        id: employeeId
      });
      return result.results;
    },
    enabled: !!employeeId,
    staleTime: 2 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false
  });
};

export function useGetEmployeeGeneralData(employeeId: string, options = {}) {
  return useQuery({
    queryKey: ['employee', employeeId, 'general-data'],
    queryFn: () => apiClient.employees.employeesGeneralDataRetrieve({ id: employeeId }),
    ...options
  });
}

export function useGetEmployeePositionData(employeeId: string, options = {}) {
  return useQuery({
    queryKey: ['employee', employeeId, 'position-data'],
    queryFn: () => apiClient.employees.employeesPositionDataRetrieve({ id: employeeId }),
    ...options
  });
}
